import { useQuery } from "@/api/utils/useQuery"
import { HasuraQueries } from "@/api/hasura/queries"
import { QueryResult } from "@/api/utils/types"
import {
  GetAdminEmailChartDataQuery,
  GetAdminEmailListQuery,
  GetAdminEmailTemplatesQuery,
  GetAdminEmailTotalCountQuery,
} from "@/api/hasura/generated/graphql"
import {
  EmailChartPoint,
  EmailChartPointParser,
  EmailTemplateParser,
  EmailVolumeParser,
} from "@/models/admin/emailVolume/emailVolume.parser"
import {
  IEmailTemplate,
  IEmailVolume,
} from "@/models/admin/emailVolume/emailVolume.types"

export type EmailListResult = {
  records: IEmailVolume[]
  totalCount: number
}

export type Filters = {
  from?: string
  to?: string
  communicationName?: string[]
}

export function buildFilters({ from, to, communicationName }: Filters) {
  return {
    processedAt: from || to ? { _gte: from, _lte: to } : undefined,
    communicationName:
      communicationName && communicationName.length > 0
        ? { _in: communicationName }
        : {},
  }
}

class EmailVolumesServiceClass {
  getEmailList(
    offset: number,
    limit: number,
    filters: Filters,
  ): QueryResult<EmailListResult> {
    return useQuery({
      ...HasuraQueries.emailVolumes.list(offset, limit, buildFilters(filters)),
      useRawResponse: true,
      placeholderData: { records: [], totalCount: 0 },
      parserFn: (data: GetAdminEmailListQuery): EmailListResult => ({
        records: data.email_messages.map((item) =>
          EmailVolumeParser.toDomain(item),
        ),
        totalCount: data.email_messages_aggregate?.aggregate?.count ?? 0,
      }),
    })
  }

  getTotalCount(filters: Filters): QueryResult<number> {
    return useQuery({
      ...HasuraQueries.emailVolumes.totalCount(buildFilters(filters)),
      useRawResponse: true,
      placeholderData: 0,
      parserFn: (data: GetAdminEmailTotalCountQuery): number =>
        data.email_messages_aggregate?.aggregate?.count ?? 0,
    })
  }

  getChartData(filters: Filters): QueryResult<EmailChartPoint[]> {
    return useQuery({
      ...HasuraQueries.emailVolumes.chartData(buildFilters(filters)),
      useRawResponse: true,
      placeholderData: [],
      parserFn: (data: GetAdminEmailChartDataQuery): EmailChartPoint[] =>
        data.email_messages.map((item) => EmailChartPointParser.toDomain(item)),
    })
  }

  getTemplates(): QueryResult<IEmailTemplate[]> {
    return useQuery({
      ...HasuraQueries.emailVolumes.templates(),
      useRawResponse: true,
      placeholderData: [],
      parserFn: (data: GetAdminEmailTemplatesQuery): IEmailTemplate[] =>
        data.requests.map((item) => EmailTemplateParser.toDomain(item)),
    })
  }
}

export const EmailVolumesService = new EmailVolumesServiceClass()
